import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import PageHeader from "@/components/common/PageHeader";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Card } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { FileText, Save, Download } from "lucide-react";
import { API } from "@/lib/api";

// The 44 clauses of Form 3CD, grouped for a smoother wizard experience.
const CLAUSE_GROUPS = [
  { title: "General", clauses: [
    { n: 8,  q: "Nature of business or profession" },
    { n: 9,  q: "Books of account (list of registers maintained)" },
    { n: 10, q: "Method of accounting (mercantile/cash)" },
    { n: 11, q: "Method of valuation of closing stock" },
    { n: 12, q: "Deviation from prescribed accounting standards" },
    { n: 13, q: "Method of accounting for previous year (any change)" },
    { n: 14, q: "Amounts not credited to P&L (44AD/44AE/etc.)" },
  ]},
  { title: "P&L Items", clauses: [
    { n: 15, q: "Adjustments for section 43CA/50C (property transfer)" },
    { n: 16, q: "Amounts admissible as deduction u/s 32AC/32AD/33AB..." },
    { n: 17, q: "Amounts debited to P&L being capital expenditure" },
    { n: 18, q: "Depreciation admissible under Income-tax Act (block-of-asset)" },
    { n: 19, q: "Amounts admissible u/s 33AB, 33ABA, 35(1)(i)..." },
    { n: 20, q: "Bonus / commission to employees (43B)" },
    { n: 21, q: "Amounts inadmissible u/s 40(a) / 40A(3) / 40(b) / 40A(9)" },
  ]},
  { title: "Loans, Deposits, TDS", clauses: [
    { n: 22, q: "Interest inadmissible u/s 23 of MSME Act 2006" },
    { n: 23, q: "Payments to specified persons u/s 40A(2)(b)" },
    { n: 24, q: "Amounts deemed to be profits u/s 32AC/33AB..." },
    { n: 25, q: "Amounts of profits chargeable to tax u/s 41" },
    { n: 26, q: "Section 43B — Payment on actual basis" },
    { n: 27, q: "CENVAT credits availed / utilised" },
    { n: 28, q: "Share application money — issue price vs FMV (56(2)(viib))" },
    { n: 29, q: "Amount of gift u/s 56(2)(x)" },
    { n: 30, q: "Details of loans/deposits u/s 269SS / 269T / 269ST" },
    { n: 31, q: "Loans/Deposits u/s 269SS / 269T (already required)" },
  ]},
  { title: "MAT, TDS, GST", clauses: [
    { n: 32, q: "Brought forward losses / depreciation" },
    { n: 33, q: "Chapter VI-A deductions (80-C to 80-U)" },
    { n: 34, q: "TDS / TCS deductions (26A / 26B compliance)" },
    { n: 35, q: "Quantitative details of principal items (RM/FG/traded)" },
    { n: 36, q: "Amount of tax on distributed profits (115-O)" },
    { n: 37, q: "Cost audit report" },
    { n: 38, q: "Central Excise audit report" },
    { n: 39, q: "Service tax audit report" },
    { n: 40, q: "Accounting ratios (turnover, GP, NP, stock-in-trade)" },
    { n: 41, q: "Demand raised / refund issued during year (any tax law)" },
    { n: 42, q: "Furnishing of Form 61 / 61A / 61B" },
    { n: 43, q: "Country-by-Country Report (286)" },
    { n: 44, q: "GST break-up — expenditure with registered / unregistered" },
  ]},
];

export default function TaxAudit() {
  const [fy, setFy] = useState("2025-26");
  const [formType, setFormType] = useState("3CB");
  const [clauses, setClauses] = useState({});
  const [auditor, setAuditor] = useState({ auditor_name: "", auditor_mem_no: "", firm_name: "", frn: "" });

  useEffect(() => { (async () => {
    const r = await api.get("/tax-audit/3cd", { params: { fy } });
    setClauses(r.data.clauses || {});
    setFormType(r.data.form_type || "3CB");
    setAuditor({
      auditor_name: r.data.auditor_name || "", auditor_mem_no: r.data.auditor_mem_no || "",
      firm_name: r.data.firm_name || "", frn: r.data.frn || "",
    });
  })(); }, [fy]);

  const setC = (n, v) => setClauses({ ...clauses, [n]: v });
  const save = async () => {
    try {
      await api.post("/tax-audit/3cd", { fy, clauses, form_type: formType, ...auditor });
      toast.success("3CD draft saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const prefill = async () => {
    try {
      const r = await api.get("/tax-audit/3cd-prefill", { params: { fy } });
      setClauses({
        ...clauses,
        13: `Method of accounting: mercantile`,
        17: `${r.data.clause_17_capex?.asset_count || 0} assets on register`,
        44: `Invoices: ${r.data.clause_44_gst_reconcile?.invoice_count} · Bills: ${r.data.clause_44_gst_reconcile?.bill_count}`,
      });
      toast.success("Ledger data pre-filled into clauses 13, 17, 44");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const exportJson = async () => {
    const res = await fetch(`${API}/tax-audit/3cd.json?fy=${fy}`, { credentials: "include" });
    if (!res.ok) return toast.error("Save first, then export");
    const blob = await res.blob(); const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `${formType}-3CD_${fy}.json`; a.click();
    URL.revokeObjectURL(url); toast.success("JSON downloaded");
  };

  const filledCount = Object.values(clauses).filter((v) => v && String(v).trim()).length;

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="tax-audit-page">
      <PageHeader eyebrow="Compliance" title="Tax Audit · Form 3CA / 3CB + 3CD"
        description="Per-clause data entry with ledger pre-fill; export CBDT utility-compatible JSON for e-filing." />
      <Card className="p-4 rounded-sm flex items-end gap-3 flex-wrap">
        <div><Label>Financial Year</Label><Input value={fy} onChange={(e) => setFy(e.target.value)} className="rounded-sm mt-1 w-32" data-testid="ta-fy" /></div>
        <div><Label>Report Form</Label>
          <div className="flex gap-1 mt-1">
            <button onClick={() => setFormType("3CA")} className={`rounded-sm px-3 h-9 text-sm ${formType === "3CA" ? "bg-accent text-white" : "bg-muted"}`} data-testid="ta-3ca">3CA (with SA)</button>
            <button onClick={() => setFormType("3CB")} className={`rounded-sm px-3 h-9 text-sm ${formType === "3CB" ? "bg-accent text-white" : "bg-muted"}`} data-testid="ta-3cb">3CB (standalone)</button>
          </div>
        </div>
        <div className="flex-1" />
        <div className="text-xs text-muted-foreground mr-2"><b>{filledCount}</b> / 37 clauses filled</div>
        <Button variant="outline" onClick={prefill} className="rounded-sm gap-1.5" data-testid="ta-prefill"><FileText className="w-4 h-4" /> Ledger Pre-fill</Button>
        <Button variant="outline" onClick={save} className="rounded-sm gap-1.5" data-testid="ta-save"><Save className="w-4 h-4" /> Save Draft</Button>
        <Button className="rounded-sm bg-accent text-white gap-1.5" onClick={exportJson} data-testid="ta-export"><Download className="w-4 h-4" /> Export JSON (CBDT)</Button>
      </Card>

      <Tabs defaultValue="0">
        <TabsList className="rounded-sm flex-wrap h-auto">
          {CLAUSE_GROUPS.map((g, i) => <TabsTrigger key={i} value={String(i)} className="rounded-sm">{g.title} · {g.clauses.length}</TabsTrigger>)}
          <TabsTrigger value="cert" className="rounded-sm">Auditor</TabsTrigger>
        </TabsList>
        {CLAUSE_GROUPS.map((g, gi) => (
          <TabsContent key={gi} value={String(gi)} className="mt-4 space-y-3">
            {g.clauses.map((c) => (
              <Card key={c.n} className="p-4 rounded-sm">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-[10px] font-mono uppercase tracking-widest bg-accent/10 text-accent px-2 py-0.5 rounded-sm">Clause {c.n}</span>
                  {clauses[c.n] && <span className="text-[10px] uppercase text-emerald-600">Filled</span>}
                </div>
                <div className="text-sm font-medium mb-2">{c.q}</div>
                <Textarea value={clauses[c.n] || ""} onChange={(e) => setC(c.n, e.target.value)} className="rounded-sm min-h-[70px] text-sm" placeholder="Details / N.A. / NIL" data-testid={`clause-${c.n}`} />
              </Card>
            ))}
          </TabsContent>
        ))}
        <TabsContent value="cert" className="mt-4">
          <Card className="p-5 rounded-sm grid md:grid-cols-2 gap-3">
            <div><Label>Auditor Name</Label><Input value={auditor.auditor_name} onChange={(e) => setAuditor({ ...auditor, auditor_name: e.target.value })} className="rounded-sm mt-1" data-testid="ta-aud-name" /></div>
            <div><Label>Membership No.</Label><Input value={auditor.auditor_mem_no} onChange={(e) => setAuditor({ ...auditor, auditor_mem_no: e.target.value })} className="rounded-sm mt-1 font-mono" data-testid="ta-aud-mem" /></div>
            <div><Label>Firm Name</Label><Input value={auditor.firm_name} onChange={(e) => setAuditor({ ...auditor, firm_name: e.target.value })} className="rounded-sm mt-1" data-testid="ta-firm" /></div>
            <div><Label>Firm Registration No. (FRN)</Label><Input value={auditor.frn} onChange={(e) => setAuditor({ ...auditor, frn: e.target.value })} className="rounded-sm mt-1 font-mono" data-testid="ta-frn" /></div>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
